import { useState } from 'react';
import type { CampaignFilters } from '../types/campaign';
import { formatCurrency } from '../lib/formatters';

interface Props {
  filters: CampaignFilters;
  onChange: (filters: CampaignFilters) => void;
  onReset: () => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
}

export function FilterBar({ filters, onChange, onReset, searchTerm, onSearchChange }: Props) {
  const [spendError, setSpendError] = useState<string | null>(null);

  // Count active filters
  const activeFilterCount = [
    searchTerm,
    filters.platform,
    filters.status,
    filters.funnelStage,
    filters.minSpend,
    filters.maxSpend,
  ].filter(Boolean).length;

  // Handle spend filter changes with validation
  const handleMinSpendChange = (value: string) => {
    const num = value === '' ? null : parseFloat(value);
    if (num !== null && filters.maxSpend !== null && num > filters.maxSpend) {
      setSpendError('Min spend must be ≤ Max spend');
    } else {
      setSpendError(null);
      onChange({ ...filters, minSpend: num });
    }
  };

  const handleMaxSpendChange = (value: string) => {
    const num = value === '' ? null : parseFloat(value);
    if (num !== null && filters.minSpend !== null && filters.minSpend > num) {
      setSpendError('Min spend must be ≤ Max spend');
    } else {
      setSpendError(null);
      onChange({ ...filters, maxSpend: num });
    }
  };

  return (
    <div className="mb-6 bg-white rounded-xl shadow p-4 dark:bg-gray-800">
      {/* Active Filters Summary */}
      {activeFilterCount > 0 && (
        <div className="mb-4 flex items-center justify-between pb-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Active filters ({activeFilterCount}):
            </span>
            {searchTerm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 dark:bg-blue-900 dark:text-blue-300 rounded text-xs">
                Search: "{searchTerm}"
                <button
                  onClick={() => onSearchChange('')}
                  className="hover:text-blue-900 dark:hover:text-blue-100"
                  title="Clear search"
                >
                  ×
                </button>
              </span>
            )}
            {filters.platform && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300 rounded text-xs capitalize">
                Platform: {filters.platform}
                <button
                  onClick={() => onChange({ ...filters, platform: null })}
                  className="hover:text-green-900 dark:hover:text-green-100"
                  title="Clear platform filter"
                >
                  ×
                </button>
              </span>
            )}
            {filters.status && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 dark:bg-purple-900 dark:text-purple-300 rounded text-xs capitalize">
                Status: {filters.status}
                <button
                  onClick={() => onChange({ ...filters, status: null })}
                  className="hover:text-purple-900 dark:hover:text-purple-100"
                  title="Clear status filter"
                >
                  ×
                </button>
              </span>
            )}
            {filters.funnelStage && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-indigo-100 text-indigo-700 dark:bg-indigo-900 dark:text-indigo-300 rounded text-xs capitalize">
                Funnel: {filters.funnelStage}
                <button
                  onClick={() => onChange({ ...filters, funnelStage: null })}
                  className="hover:text-indigo-900 dark:hover:text-indigo-100"
                  title="Clear funnel filter"
                >
                  ×
                </button>
              </span>
            )}
            {(filters.minSpend !== null || filters.maxSpend !== null) && (
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 dark:bg-amber-900 dark:text-amber-300 rounded text-xs">
                Spend: {filters.minSpend !== null && formatCurrency(filters.minSpend)}
                {filters.minSpend !== null && filters.maxSpend !== null && ' - '}
                {filters.maxSpend !== null && formatCurrency(filters.maxSpend)}
                <button
                  onClick={() => onChange({ ...filters, minSpend: null, maxSpend: null })}
                  className="hover:text-amber-900 dark:hover:text-amber-100"
                  title="Clear spend filter"
                >
                  ×
                </button>
              </span>
            )}
          </div>
          <button
            onClick={onReset}
            className="text-sm text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 font-medium"
          >
            Clear all
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-4">
        {/* Search */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Search
          </label>
          <div className="relative">
            <input
              type="text"
              value={searchTerm}
              onChange={(e) => onSearchChange(e.target.value)}
              placeholder="Search campaigns..."
              className="w-full px-4 py-2 pl-10 border border-gray-300 rounded-lg dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <svg
              className="absolute left-3 top-3 w-4 h-4 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
              />
            </svg>
          </div>
        </div>

        {/* Platform Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Platform
          </label>
          <select
            value={filters.platform || ''}
            onChange={(e) => onChange({ ...filters, platform: e.target.value || null })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Platforms</option>
            <option value="meta">Meta</option>
            <option value="google">Google Ads</option>
            <option value="tiktok">TikTok</option>
          </select>
        </div>

        {/* Status Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Status
          </label>
          <select
            value={filters.status || ''}
            onChange={(e) => onChange({ ...filters, status: e.target.value || null })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="paused">Paused</option>
            <option value="deleted">Deleted</option>
            <option value="archived">Archived</option>
          </select>
        </div>

        {/* Funnel Stage Filter (E007-S006) */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Funnel Stage
          </label>
          <select
            value={filters.funnelStage || ''}
            onChange={(e) => onChange({ ...filters, funnelStage: e.target.value || null })}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg dark:bg-gray-900 dark:border-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500"
          >
            <option value="">All Stages</option>
            <option value="awareness">🎯 Awareness</option>
            <option value="consideration">🤔 Consideration</option>
            <option value="conversion">💰 Conversion</option>
          </select>
        </div>

        {/* Quick Filters */}
        <div className="flex items-end">
          <div className="w-full flex gap-2">
            <button
              onClick={() => onChange({ ...filters, status: 'active' })}
              className="flex-1 px-3 py-2 text-xs bg-green-50 text-green-700 rounded-lg hover:bg-green-100 dark:bg-green-900/20 dark:text-green-400 dark:hover:bg-green-900/30 transition-colors"
              title="Show only active campaigns"
            >
              ✓ Active
            </button>
            <button
              onClick={() => onChange({ ...filters, status: 'paused' })}
              className="flex-1 px-3 py-2 text-xs bg-yellow-50 text-yellow-700 rounded-lg hover:bg-yellow-100 dark:bg-yellow-900/20 dark:text-yellow-400 dark:hover:bg-yellow-900/30 transition-colors"
              title="Show only paused campaigns"
            >
              ⏸ Paused
            </button>
          </div>
        </div>
      </div>

      {/* Spend Filters Row */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 mt-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Min Spend
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-gray-400 dark:text-gray-500">$</span>
            <input
              type="number"
              min="0"
              step="100"
              value={filters.minSpend ?? ''}
              onChange={(e) => handleMinSpendChange(e.target.value)}
              placeholder="0"
              className={`w-full px-4 py-2 pl-7 border rounded-lg dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-2 ${
                spendError
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 dark:border-gray-700 focus:ring-blue-500'
              }`}
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Max Spend
          </label>
          <div className="relative">
            <span className="absolute left-3 top-2.5 text-gray-400 dark:text-gray-500">$</span>
            <input
              type="number"
              min="0"
              step="100"
              value={filters.maxSpend ?? ''}
              onChange={(e) => handleMaxSpendChange(e.target.value)}
              placeholder="No limit"
              className={`w-full px-4 py-2 pl-7 border rounded-lg dark:bg-gray-900 dark:text-white focus:outline-none focus:ring-2 ${
                spendError
                  ? 'border-red-500 focus:ring-red-500'
                  : 'border-gray-300 dark:border-gray-700 focus:ring-blue-500'
              }`}
            />
          </div>
        </div>

        <div className="flex items-end">
          {spendError && (
            <div className="text-sm text-red-600 dark:text-red-400 py-2" role="alert">
              {spendError}
            </div>
          )}
          {!spendError && (filters.minSpend !== null || filters.maxSpend !== null) && (
            <div className="text-sm text-gray-600 dark:text-gray-400 py-2">
              Filtering by spend range
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
